import { Component, OnInit, OnDestroy } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { Subscription } from 'rxjs';
import { SocialService, SocialAccount, SocialNetwork } from '../../../services/social.service';

interface NetworkInfo {
  id: SocialNetwork;
  name: string;
  color: string;
  icon: string;
}

@Component({
  selector: 'app-social-accounts',
  templateUrl: './social-accounts.component.html',
  styleUrls: ['./social-accounts.component.scss']
})
export class SocialAccountsComponent implements OnInit, OnDestroy {

  accounts: SocialAccount[] = [];
  loading = false;
  connectingNetwork: SocialNetwork | null = null;
  errorMessage: string | null = null;
  facebookPageId: string = '';

  readonly networks: NetworkInfo[] = [
    { id: 'INSTAGRAM', name: 'Instagram', color: '#E1306C', icon: '📷' },
    { id: 'FACEBOOK',  name: 'Facebook',  color: '#1877F2', icon: '👥' },
    { id: 'LINKEDIN',  name: 'LinkedIn',  color: '#0A66C2', icon: '💼' },
    { id: 'TWITTER',   name: 'Twitter',   color: '#1DA1F2', icon: '🐦' },
    { id: 'YOUTUBE',   name: 'YouTube',   color: '#FF0000', icon: '▶️' },
    { id: 'TIKTOK', name: 'TikTok', color: '#000000', icon: '🎵' }
  ];

  // URLs fijas al perfil real de cada red (no se derivan de los datos de la
  // cuenta conectada: para Instagram/TikTok/LinkedIn ese dato no se guarda
  // de forma confiable — ver DOCUMENTACION — así que se configuran a mano).
  private readonly profileUrls: Partial<Record<SocialNetwork, string>> = {
    FACEBOOK: 'https://www.facebook.com/profile.php?id=61590411062768',
    TIKTOK: 'https://www.tiktok.com/@nectuminvestment',
    TWITTER: 'https://x.com/Ni_software',
    LINKEDIN: 'https://www.linkedin.com/in/carlos-lopez-herce/',
    INSTAGRAM: 'https://www.instagram.com/ni_software/',
    YOUTUBE: 'https://www.youtube.com/@NiSoftware',
  };

  private routeSub?: Subscription;

  constructor(
    private socialService: SocialService,
    private route: ActivatedRoute,
    private router: Router,
  ) {}

  ngOnInit(): void {
    this.loadAccounts();
    // Si venimos del callback de OAuth, completar el flujo
    this.routeSub = this.route.queryParams.subscribe(params => {
        let network = params['network'];
        if (this.router.url.startsWith('/social/accounts/tiktok')) {
          network = 'TIKTOK';
        }
        if (params['code'] && network) {
          this.handleOAuthCallback(network, params['code'], params['state']);
        }
    });
  }

  ngOnDestroy(): void {
    this.routeSub?.unsubscribe();
  }

  loadAccounts(): void {
    this.loading = true;
    this.socialService.getAccounts().subscribe({
      next: (accounts) => {
        this.accounts = accounts;
        this.loading = false;
      },
      error: (err) => {
        console.error(err);
        this.loading = false;
      }
    });
  }

  getAccountFor(network: SocialNetwork): SocialAccount | undefined {
    return this.accounts.find(a => a.network === network && a.active);
  }

  profileUrl(network: SocialNetwork): string | undefined {
    return this.profileUrls[network];
  }

  isConnected(network: SocialNetwork): boolean {
    return !!this.getAccountFor(network);
  }

connect(network: SocialNetwork): void {

  this.connectingNetwork = network;
  this.errorMessage = null;

  const redirectUri =
    network === 'TIKTOK'
      ? `${window.location.origin}/social/accounts/tiktok`
      : `${window.location.origin}/social/accounts?network=${network}`;

    this.socialService.startOAuth(network, redirectUri).subscribe({

      next: (res) => {

        if (network === 'TWITTER' && res.codeVerifier) {
          sessionStorage.setItem('twitter_code_verifier',res.codeVerifier);
        }

        if (network === 'TIKTOK' && res.codeVerifier) {
          sessionStorage.setItem('tiktok_code_verifier',res.codeVerifier);
        }
        window.location.href = res.authorizationUrl;
      },

      error: (err) => {

        this.errorMessage = 'Error al iniciar conexión: ' + (err.error?.message || err.message);

        this.connectingNetwork = null;
      }
    });
}

  disconnect(account: SocialAccount): void {
    const net = this.networks.find(n => n.id === account.network);
    if (!confirm(`¿Desconectar ${net?.name}? Los posts programados para esta red fallarán.`)) return;

    this.socialService.disconnectAccount(account.accountId).subscribe({
      next: () => this.loadAccounts(),
      error: (err) => alert('Error al desconectar: ' + (err.error?.message || err.message))
    });
  }

  connectStaticFacebook(): void {
    this.connectingNetwork = 'FACEBOOK';
    this.errorMessage = null;
    this.socialService.setupStaticFacebook().subscribe({
      next: () => {
        this.connectingNetwork = null;
        this.loadAccounts();
      },
      error: (err) => {
        this.errorMessage = 'Error al activar token estático: ' + (err.error?.message || err.message);
        this.connectingNetwork = null;
      }
    });
  }

  private handleOAuthCallback(network: string,code: string,state: string): void {

    const redirectUri =
      network === 'TIKTOK'
        ? `${window.location.origin}/social/accounts/tiktok`
        : `${window.location.origin}/social/accounts?network=${network}`;

    this.connectingNetwork = network as SocialNetwork;

    const codeVerifier =
      network === 'TWITTER'
        ? sessionStorage.getItem('twitter_code_verifier') || undefined
        : network === 'TIKTOK'
          ? sessionStorage.getItem('tiktok_code_verifier') || undefined
          : undefined;

    this.socialService.completeOAuth(network as SocialNetwork,code,state,redirectUri,undefined,codeVerifier).subscribe({

      next: () => {

        if (network === 'TWITTER') {
          sessionStorage.removeItem('twitter_code_verifier');
        }

        if (network === 'TIKTOK') {
          sessionStorage.removeItem('tiktok_code_verifier');
        }

        this.router.navigate(
          ['/social/accounts'],
          { replaceUrl: true }
        );

        this.connectingNetwork = null;
        this.loadAccounts();
      },

      error: (err) => {

        if (network === 'TWITTER') {
          sessionStorage.removeItem('twitter_code_verifier');
        }

        if (network === 'TIKTOK') {
          sessionStorage.removeItem('tiktok_code_verifier');
        }

        this.errorMessage ='Error completando conexión: ' +(err.error?.message || err.message);

        this.router.navigate(['/social/accounts'],{ replaceUrl: true });

        this.connectingNetwork = null;
      }
    });
  }

  backToList(): void {
    this.router.navigate(['/social']);
  }
}

